import { CellContext, ColumnDef } from '@tanstack/react-table'
import { capitalCase } from 'change-case'
import { useTranslation } from 'react-i18next'

import { selectColor, StatusChip } from 'components/Chips/StatusChip'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import Tooltip from 'components/Tooltip'
import { KeyValueStatus } from 'features/globalSetup/api/types/workspaceKeyValueDTO'
import { DATE_FORMAT } from 'utils/date'
import { ActionsCell } from './components/ActionsCell'
import { KeyValueFreshStatus } from './hooks/useGetFreshStatusesForWorkspaceKeyValues'
import { WorkspaceKeyValueSlice } from './types'

export const useColumns = (
  statuses: KeyValueFreshStatus[],
): ColumnDef<WorkspaceKeyValueSlice>[] => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.keyValues.list.header',
  })

  return [
    {
      accessorKey: 'keyValue.type',
      cell: ({
        row: {
          original: {
            keyValue: { type },
          },
        },
      }: CellContext<WorkspaceKeyValueSlice, unknown>): string => capitalCase(type),
      header: t('type'),
    },
    {
      accessorKey: 'keyValue.name',
      cell: ({
        row: {
          original: {
            keyValue: { name, tooltip },
          },
        },
      }: CellContext<WorkspaceKeyValueSlice, unknown>): JSX.Element => (
        <>
          {name}
          {tooltip && <Tooltip content={tooltip} />}
        </>
      ),
      header: t('name'),
    },
    {
      accessorKey: 'status',
      cell: ({
        row: {
          original: { id: keyValueId },
        },
      }) => {
        const status: KeyValueStatus | undefined = statuses.find(
          ({ id }) => id === keyValueId,
        )?.status

        return (
          status && (
            <StatusChip
              chipColors={theme => selectColor<KeyValueStatus>(status, theme)}
              status={status}
            />
          )
        )
      },
      header: t('status'),
    },
    {
      accessorKey: 'updatedAt',
      cell: (props: CellContext<WorkspaceKeyValueSlice, unknown>): JSX.Element => (
        <LastModifiedCell
          {...props}
          dateFormat={DATE_FORMAT.WITH_TIME}
        />
      ),
      header: t('updatedAt'),
    },
    {
      accessorKey: 'actions',
      cell: (props: CellContext<WorkspaceKeyValueSlice, unknown>): JSX.Element => (
        <ActionsCell {...props} />
      ),
      header: t('actions'),
      size: COLUMN_SIZE.SM,
    },
  ]
}
